/**
 * Leitura do conteúdo de uma aprovação para o cartão: evidência "Baseado em…", citação do cliente de um
 * compromisso, sugestão de resposta da pergunta da IA e o corpo de erro da API. Puro, sem I/O.
 */

const EVIDENCE_MARKER = /^baseado em/i;

/** Separa as linhas "Baseado em…" (evidência que o backend anexa ao corpo) do texto da proposta. */
export function splitEvidence(body: string | null | undefined): { text: string; evidence: string[] } {
  const evidence: string[] = [];
  const kept: string[] = [];
  for (const raw of (body ?? '').split('\n')) {
    const line = raw.trim();
    if (EVIDENCE_MARKER.test(line)) {
      if (!evidence.includes(line)) evidence.push(line);
    } else {
      kept.push(raw);
    }
  }
  return { text: kept.join('\n').trim(), evidence };
}

const QUOTE = /“([^”]+)”/;

/** execute_commitment: a fala do cliente vai no suggestedAction entre aspas curvas (CommitmentApprovalQueue). */
export function commitmentQuote(suggestedAction: string | null | undefined): string | null {
  const m = QUOTE.exec(suggestedAction ?? '');
  return m ? m[1].trim() || null : null;
}

const SENDS_AT = /Sai em (\d{2}\/\d{2} às \d{2}:\d{2})/i;

/** execute_commitment: horário de envio ("Sai em dd/MM às HH:mm.") que o backend põe no suggestedAction. */
export function commitmentSendsAt(suggestedAction: string | null | undefined): string | null {
  return SENDS_AT.exec(suggestedAction ?? '')?.[1] ?? null;
}

const DATE_DEFAULT = /Posso mandar (.+?)\.?$/;

/**
 * Chip de resposta em 1 clique: só quando a pergunta é APENAS sobre a data e traz o default sugerido
 * ("Posso mandar amanhã às 9h."). A resposta "sim" faz o backend usar exatamente o instante sugerido.
 * Qualquer outro formato ⇒ null (nunca adivinhar a resposta do vendedor).
 */
export function suggestionFromQuestion(
  question: string | null | undefined,
  missingSlots: string[] | null | undefined,
): { chip: string; answer: string } | null {
  const slots = missingSlots ?? [];
  if (slots.length !== 1 || slots[0] !== 'date') return null;
  const m = DATE_DEFAULT.exec((question ?? '').trim());
  if (!m || !m[1].trim()) return null;
  return { chip: `Pode ser ${m[1].trim()}`, answer: 'sim' };
}

export interface ApiErrorInfo {
  status: number | null;
  code: string | null;
  reason: string | null;
  message: string | null;
}

/** Corpo de erro do MendsVisionController ({ message, code, reason? }), camel ou Pascal. */
export function readApiError(error: unknown): ApiErrorInfo {
  const response = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
  const data = (response?.data && typeof response.data === 'object' ? response.data : {}) as Record<string, unknown>;
  const pick = (key: string): unknown => data[key] ?? data[key.charAt(0).toUpperCase() + key.slice(1)];
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    code: str(pick('code')),
    reason: str(pick('reason')),
    message: str(pick('message')),
  };
}


/** Decisão de desconto do Registrar: percentual em edição (aprovado, senão o pedido) como texto do campo. */
export function discountPercentOf(item: { actionKind?: string | null; actionPayload?: string | null }): string {
  if ((item.actionKind || '').trim().toLowerCase() !== 'discount_decision') return '';
  try {
    const p = JSON.parse(item.actionPayload || '{}');
    const v = typeof p.approvedPercent === 'number' ? p.approvedPercent : p.requestedPercent;
    return typeof v === 'number' ? String(v).replace('.', ',') : '';
  } catch {
    return '';
  }
}

/**
 * Payload editado da decisão de desconto: o percentual final vai em approvedPercent, o pedido original fica em
 * requestedPercent (REG-31). undefined = nada a mudar no payload (outro tipo, campo vazio ou número inválido).
 */
export function discountEditPayload(
  item: { actionKind?: string | null; actionPayload?: string | null },
  percentText: string,
): string | undefined {
  if ((item.actionKind || '').trim().toLowerCase() !== 'discount_decision') return undefined;
  const n = Number((percentText || '').trim().replace(',', '.'));
  if (!percentText.trim() || !Number.isFinite(n) || n < 0 || n > 100) return undefined;
  try {
    const p = JSON.parse(item.actionPayload || '{}');
    return JSON.stringify({ ...p, approvedPercent: n });
  } catch {
    return undefined;
  }
}
